import React from "react";
import {
  ControlButtons,
  ControlTooltip,
} from "@learningblocks/storybook-components/lib/ControlTooltip";
import graphics from "../helpers/graphics";
import ModalManager from "../managers/modalManager";
import SimManager from "../managers/simManager";
import {
  ActionButton,
  Label,
  TwoLineLabel,
} from "../types/ObjectDefinitionTypes";
import { HamburgerIcon } from "@learningblocks/storybook-components/lib/Icons";
import "./styles.scss";

/**
 * Shared utility function to trigger button actions.
 * Used by both InteractableButtons and CompositeInteractableObject.
 * @param button - The button configuration
 * @param scope - The object ID scope
 * @param changeTextFn - Optional custom function to handle text updates (for composite objects)
 */
export const triggerButtonAction = (
  button: ActionButton,
  scope: string,
  changeTextFn?: (text: string, name?: string) => void
) => {
  console.log(" => clicked on button ", button, scope, changeTextFn);
  if (button.type === "Modal") {
    ModalManager.openModal(scope, button.modal);
  }
  if (button.actionName) {
    SimManager.dispatchAction(button.actionName, scope);
  }
  if (button.type === "UpdateState" && button.stateUpdates) {
    button.stateUpdates.forEach((update) => {
      if (update.nextState) {
        SimManager.updateObjectState(
          update.objectId || scope,
          update.nextState,
          update.position
        );
      }
      if (update.textUpdate) {
        if (changeTextFn) {
          changeTextFn(update.textUpdate.value, update.textUpdate.name);
        } else {
          SimManager.getInstance().setText(
            update.objectId || scope,
            update.textUpdate.value,
            update.textUpdate.name
          );
        }
      }
    });
  }
  if (button.type === "Eject") {
    SimManager.getInstance().ejectButtonFunction(button.itemId || "");
  }
};

interface InteractableButtonsProps {
  buttons?: ActionButton[];
  visible?: boolean;
  label?: string | Label | TwoLineLabel;
  scope: string;
  scale?: string | number;
  moveMenu?: { [key: string]: any };
  onOpenMoveMenu?: () => void;
  onSetHamburgerButtonRef?: (ref: React.RefObject<HTMLElement>) => void;
}

export const InteractableButtons = ({
  buttons,
  visible,
  label,
  scope,
  scale,
  moveMenu,
  onOpenMoveMenu,
  onSetHamburgerButtonRef,
}: InteractableButtonsProps) => {
  const [hovered, setHovered] = React.useState(false);
  const [showLabel, setShowLabel] = React.useState(true);
  const hamburgerButtonRef = React.useRef<HTMLElement | null>(null);

  // Callback ref to handle when the hamburger button element is set
  const setHamburgerButtonElement = React.useCallback(
    (element: HTMLElement | null) => {
      if (element && hamburgerButtonRef.current !== element) {
        hamburgerButtonRef.current = element;
        if (moveMenu && onSetHamburgerButtonRef) {
          onSetHamburgerButtonRef(hamburgerButtonRef);
        }
      }
    },
    [moveMenu, onSetHamburgerButtonRef]
  );

  const onButtonClick = (button: ActionButton) => {
    triggerButtonAction(button, scope);
  };
  const buttonList = React.useMemo(() => {
    const list = [];

    // Add hamburger menu button if moveMenu is available
    if (moveMenu && onOpenMoveMenu) {
      list.push({
        image: HamburgerIcon,
        onClick: onOpenMoveMenu,
        altText: "Open Move Menu",
        buttonRef: setHamburgerButtonElement,
      });
    }

    // Add regular buttons
    if (buttons) {
      buttons.forEach((button) => {
        if (button.icon) {
          const image = graphics.getInstance().getImage(button.icon);
          list.push({
            altText: button.title,
            image,
            onClick: () => onButtonClick?.(button),
          });
        } else {
          list.push({
            label: button.title,
            onClick: () => onButtonClick?.(button),
          });
        }
      });
    }

    return list.length > 0 ? list : undefined;
  }, [buttons, moveMenu, onOpenMoveMenu, setHamburgerButtonElement]);

  function getTooltipClasses() {
    const direction = getLabelDirection(label);
    if (!showLabel && !!buttonList && direction === "vertical")
      return `tooltip verticalButtons`;

    return `tooltip ${direction === "vertical" ? "vertical" : "horizontal"}`;
  }

  const handleMouseEnter = () => {
    setHovered(true);
    const direction = getLabelDirection(label);
    if (direction !== "vertical") setShowLabel(false);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setShowLabel(true);
  };

  React.useEffect(() => {
    if (visible) setShowLabel(false);
    else if (!visible && !hovered) setShowLabel(true);
  }, [visible]);

  // Helper function to extract offset and xOffset from any label type
  const getLabelOffsets = (
    label: string | Label | TwoLineLabel | undefined
  ) => {
    if (typeof label === "object" && label !== null) {
      return {
        offset: label.offset || 0,
        xOffset: label.xOffset,
      };
    }
    return { offset: 0, xOffset: undefined };
  };

  // Helper function to extract direction from any label type
  const getLabelDirection = (
    label: string | Label | TwoLineLabel | undefined
  ) => {
    if (typeof label === "object" && label !== null && label.direction) {
      return label.direction;
    }
    return undefined;
  };

  // Helper function to get label text or pass the whole label object for two-line labels
  const getLabelForTooltip = (
    label: string | Label | TwoLineLabel | undefined
  ) => {
    if (typeof label === "string") {
      return label;
    }
    if (typeof label === "object" && label !== null) {
      // Check if it's a two-line label
      if ("topLine" in label && "bottomLine" in label) {
        return label; // Pass the whole object for two-line labels
      }
      // Regular label object
      return (label as Label).text;
    }
    return undefined;
  };

  const labelOffsets = getLabelOffsets(label);
  const labelDirection = getLabelDirection(label);
  const labelForTooltip = getLabelForTooltip(label);

  // Map full direction string to single character for LabelTooltip component
  const labelOrientation =
    labelDirection === "vertical"
      ? "v"
      : labelDirection === "horizontal"
        ? "h"
        : undefined;

  return (
    <div
      className="interactable-tooltip"
      style={{
        bottom: labelOffsets.offset,
        scale: scale ? `calc(1 / ${scale})` : 1,
        ...(labelOffsets.xOffset ? { left: labelOffsets.xOffset } : {}),
      }}
    >
      <div
        className={getTooltipClasses()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ControlTooltip
          showControls={!showLabel && !!buttonList}
          label={labelForTooltip}
          orientation={labelOrientation}
        >
          {buttonList && <ControlButtons buttonList={buttonList} />}
        </ControlTooltip>
      </div>
    </div>
  );
};
